/**
 * Mahsulotlarni Excel orqali import qilish uchun yordamchi:
 *  - buildTemplateBlob(): tanlanadigan (dropdown) ustunlar bilan tayyor shablon yaratadi
 *  - parseProductsFromFile(): to'ldirilgan faylni o'qib, nomlarni id ga aylantiradi
 *
 * brand/model/unit/colour/country ro'yxatlari komponentdan uzatiladi (axios shu yerda emas).
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Ustunlar tartibi (sarlavha + kalit). Bu tartib import o'qishda ham ishlatiladi.
export const COLUMNS = [
  { key: 'extra_name',     header: 'Nomi *',         width: 38 },
  { key: 'brand',          header: 'Brend',          width: 20, list: 'brands' },
  { key: 'model',          header: 'Model',          width: 26, list: 'models' },
  { key: 'unit',           header: 'Birlik',         width: 14, list: 'units' },
  { key: 'colour',         header: 'Rang',           width: 14, list: 'colours' },
  { key: 'country',        header: 'Davlat',         width: 16, list: 'countries' },
  { key: 'quality',        header: 'Sifat',          width: 14 },
  { key: 'artikul',        header: 'Artikul',        width: 16 },
  { key: 'minimum_amount', header: 'Min. miqdor',    width: 12 },
  { key: 'barcode',        header: 'Shtrix kod (bo\'sh=avto)', width: 22 },
];

const norm = v => (v == null ? '' : String(v)).trim().toLowerCase();

// Model uchun ko'rinadigan yorliq: "Brend › Model" (brend bo'lmasa shunchaki model nomi)
function modelLabel(m, brandById) {
  const b = m.brand_id ? brandById[m.brand_id] : null;
  return b ? `${b} › ${m.name}` : (m.name || '');
}

/**
 * Tanlash (dropdown) ustunli shablonni yaratib, brauzerga yuklaydi.
 * lists = { brands:[], models:[], units:[], colours:[], countries:[] }  (har biri {id,name,brand_id?})
 */
export async function buildTemplateBlob(lists) {
  const brands    = lists.brands    || [];
  const models    = lists.models    || [];
  const units     = lists.units     || [];
  const colours   = lists.colours   || [];
  const countries = lists.countries || [];

  const brandById = {};
  brands.forEach(b => { brandById[b.id] = b.name; });

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Mahsulotlar');
  const ls = wb.addWorksheet('Royxatlar'); // dropdown manbalari (yashiriladi)

  // --- Asosiy varaq sarlavhalari ---
  ws.columns = COLUMNS.map(c => ({ header: c.header, key: c.key, width: c.width }));
  const head = ws.getRow(1);
  head.height = 22;
  head.eachCell(cell => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7C3AED' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } } };
  });
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  // --- Royxatlar varaqiga dropdown qiymatlarini yozish ---
  const listData = {
    brands:    brands.map(b => b.name),
    models:    models.map(m => modelLabel(m, brandById)),
    units:     units.map(u => u.name),
    colours:   colours.map(c => c.name),
    countries: countries.map(c => c.name),
  };
  const listCol = { brands: 'A', models: 'B', units: 'C', colours: 'D', countries: 'E' };
  ls.getCell('A1').value = 'Brendlar';
  ls.getCell('B1').value = 'Modellar';
  ls.getCell('C1').value = 'Birliklar';
  ls.getCell('D1').value = 'Ranglar';
  ls.getCell('E1').value = 'Davlatlar';
  Object.keys(listData).forEach(key => {
    const col = listCol[key];
    listData[key].forEach((val, i) => { ls.getCell(`${col}${i + 2}`).value = val; });
  });
  ls.state = 'hidden';

  // --- Dropdown (data validation) larni qo'shish: 2..1000 qatorlar ---
  const LAST = 1000;
  COLUMNS.forEach((c, idx) => {
    if (!c.list) return;
    const arr = listData[c.list];
    if (!arr || arr.length === 0) return;
    const col = listCol[c.list];
    const colLetter = String.fromCharCode(65 + idx); // asosiy varaqdagi ustun harfi
    const formula = `Royxatlar!$${col}$2:$${col}$${arr.length + 1}`;
    ws.dataValidations.add(`${colLetter}2:${colLetter}${LAST}`, {
      type: 'list',
      allowBlank: true,
      formulae: [formula],
      showErrorMessage: false, // ro'yxatda yo'q qiymat ham yozilishi mumkin (yangi brend va h.k.)
    });
  });

  // --- Namuna qator (foydalanuvchiga ko'rsatma) ---
  ws.addRow({
    extra_name: 'MISOL: ORQA AMORTIZATOR NEXIA',
    brand: brands[0] ? brands[0].name : '',
    model: models[0] ? modelLabel(models[0], brandById) : '',
    unit: units[0] ? units[0].name : '',
    colour: '',
    country: '',
    quality: '',
    artikul: '',
    minimum_amount: 1,
    barcode: '',
  });
  ws.getRow(2).font = { italic: true, color: { argb: 'FF94A3B8' } };

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, 'mahsulot_shablon.xlsx');
}

/**
 * To'ldirilgan faylni o'qib, backendga yuboriladigan mahsulotlar massivini qaytaradi.
 * Nomlar (brend/model/birlik/rang/davlat) id larga aylantiriladi.
 */
export async function parseProductsFromFile(file, lists) {
  const brands    = lists.brands    || [];
  const models    = lists.models    || [];
  const units     = lists.units     || [];
  const colours   = lists.colours   || [];
  const countries = lists.countries || [];

  const brandById = {};
  brands.forEach(b => { brandById[b.id] = b.name; });

  // Nom -> id xaritalari (kichik harfga keltirilgan)
  const brandByName = {};   brands.forEach(b => { brandByName[norm(b.name)] = b.id; });
  const unitByName = {};    units.forEach(u => { unitByName[norm(u.name)] = u.id; });
  const colourByName = {};  colours.forEach(c => { colourByName[norm(c.name)] = c.id; });
  const countryByName = {}; countries.forEach(c => { countryByName[norm(c.name)] = c.id; });
  const modelByLabel = {};  const modelByName = {};
  models.forEach(m => {
    modelByLabel[norm(modelLabel(m, brandById))] = m;
    if (modelByName[norm(m.name)] === undefined) modelByName[norm(m.name)] = m;
  });

  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const ws = wb.getWorksheet('Mahsulotlar') || wb.worksheets[0];
  if (!ws) throw new Error("Faylda varaq topilmadi");

  // Sarlavha qatori bo'yicha ustun indekslarini aniqlash (tartib o'zgarsa ham ishlasin).
  // Qavs ichidagi izoh "(...)" va "*" e'tiborga olinmaydi — eski shablonlar ham mos keladi.
  const base = h => norm(h).replace(/\s*\(.*?\)\s*/g, '').replace('*', '').trim();
  const headerRow = ws.getRow(1);
  const colIndex = {}; // key -> column number
  headerRow.eachCell((cell, colNumber) => {
    const txt = base(cell.value);
    const found = COLUMNS.find(c => base(c.header) === txt);
    if (found) colIndex[found.key] = colNumber;
  });
  // Agar sarlavha topilmasa — ustun tartibiga ko'ra (zaxira)
  if (Object.keys(colIndex).length === 0) {
    COLUMNS.forEach((c, i) => { colIndex[c.key] = i + 1; });
  }

  const cellText = (row, key) => {
    const idx = colIndex[key];
    if (!idx) return '';
    const v = row.getCell(idx).value;
    if (v == null) return '';
    if (typeof v === 'object') {
      if (v.text != null) return String(v.text).trim();        // rich text / hyperlink
      if (v.result != null) return String(v.result).trim();    // formula natijasi
      if (v.richText) return v.richText.map(t => t.text).join('').trim();
      return String(v).trim();
    }
    return String(v).trim();
  };

  const products = [];
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // sarlavha
    const extra_name = cellText(row, 'extra_name');
    // Namuna qatorini va bo'sh qatorlarni o'tkazib yuborish
    if (!extra_name || extra_name.toUpperCase().startsWith('MISOL:')) return;

    const brandCell  = cellText(row, 'brand');
    const modelCell  = cellText(row, 'model');
    const unitCell   = cellText(row, 'unit');
    const colourCell = cellText(row, 'colour');
    const countryCell= cellText(row, 'country');

    let brand_id = brandByName[norm(brandCell)] || null;
    const mObj = modelByLabel[norm(modelCell)] || modelByName[norm(modelCell)] || null;
    const model_id = mObj ? mObj.id : null;
    if (!brand_id && mObj && mObj.brand_id) brand_id = mObj.brand_id; // brend bo'sh bo'lsa modeldan ol

    products.push({
      _row: rowNumber,
      extra_name,
      name: extra_name,
      brand_id,
      model_id,
      unit_id: unitByName[norm(unitCell)] || null,
      colour_id: colourByName[norm(colourCell)] || null,
      country_id: countryByName[norm(countryCell)] || null,
      quality: cellText(row, 'quality') || null,
      artikul: cellText(row, 'artikul') || null,
      minimum_amount: cellText(row, 'minimum_amount') || null,
      barcode: cellText(row, 'barcode') || null,
    });
  });

  return products;
}
